import React, { useMemo } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import StatisticalDataTable, {
  statNumberColumn,
  statPercentColumn,
  statRankColumn
} from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalDoughnutChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import {
  computeShares,
  doughnutChartData,
  topNWithOthers,
} from './charts/chartDataHelpers';

function ReportLoadingPanel({ copy }) {
  return (
    <div className="hd-reports-panel mb-4">
      <div className="hd-reports-loading">
        <span className="spinner-border spinner-border-sm" />
        <span>{copy.LOADING}</span>
      </div>
    </div>
  );
}

function ReportEmptyPanel({ message, hint, clearFiltersLabel, onClearFilters }) {
  return (
    <div className="hd-reports-panel mb-4">
      <p className="hd-reports-empty mb-1">{message}</p>
      {hint ? <p className="text-muted mb-2">{hint}</p> : null}
      {onClearFilters ? (
        <Button variant="outline-secondary" size="sm" onClick={onClearFilters}>
          {clearFiltersLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function NursingCareReportSection({
  data,
  summary,
  loading,
  copy,
  showKpis = true,
  emptyHint = '',
  clearFiltersLabel = '',
  onClearFilters = null,
  /** Vue injections : même structure de ranking, autre KPI / libellés. */
  mode = 'care'
}) {
  const isInjection = mode === 'injection';
  const ranking = isInjection
    ? data?.nursing?.injection_ranking || data?.nursing?.care_ranking || []
    : data?.nursing?.care_ranking || [];
  const countKey = isInjection ? 'nursing_injection_actions' : 'nursing_care_actions';
  const titleCol = isInjection
    ? copy.COL_NURSING_INJECTION || copy.COL_INJECTION || copy.COL_NURSING_CARE
    : copy.COL_NURSING_CARE;
  const emptyMessage = isInjection
    ? copy.EMPTY_NURSING_INJECTIONS || copy.EMPTY_NURSING_CARE
    : copy.EMPTY_NURSING_CARE;
  const tableTitle = isInjection
    ? copy.TABLE_NURSING_INJECTION_TITLE || copy.TABLE_NURSING_CARE_TITLE
    : copy.TABLE_NURSING_CARE_TITLE;
  const pieTitle = isInjection
    ? copy.CHART_NURSING_INJECTION_PIE_TITLE || copy.CHART_NURSING_CARE_PIE_TITLE
    : copy.CHART_NURSING_CARE_PIE_TITLE;
  const pieDesc = isInjection
    ? copy.CHART_NURSING_INJECTION_PIE_DESC || copy.CHART_NURSING_CARE_PIE_DESC
    : copy.CHART_NURSING_CARE_PIE_DESC;
  const kpiTitle = isInjection
    ? copy.KPI_NURSING_INJECTIONS || copy.KPI_NURSING_CARE
    : copy.KPI_NURSING_CARE;

  const careRows = useMemo(() => computeShares(ranking, 'action_count'), [ranking]);

  const carePie = useMemo(() => {
    const pie = topNWithOthers(careRows, 'action_count', 'care_label', 5, copy.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [careRows, copy.CHART_OTHERS]);

  const careTableCols = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    {
      key: 'care_label',
      label: titleCol,
      className: 'fw-semibold',
      render: (r) => r.care_label
    },
    statRankColumn(copy.COL_ACTIONS, 'action_count'),
    statPercentColumn(copy.COL_SHARE),
    statNumberColumn(copy.COL_CONSULTATIONS, 'consultation_count')
  ];

  const empty = Number(summary?.[countKey] || 0) === 0;

  return (
    <>
      {showKpis ? (
        <Row className="g-3 mb-4">
          <Col sm={4}>
            <HeadDoctorStatCard
              title={kpiTitle}
              value={summary?.[countKey]}
              icon="user-md"
              tone="indigo"
              loading={loading}
            />
          </Col>
        </Row>
      ) : null}

      {loading ? (
        <ReportLoadingPanel copy={copy} />
      ) : empty ? (
        <ReportEmptyPanel
          message={emptyMessage}
          hint={emptyHint}
          clearFiltersLabel={clearFiltersLabel}
          onClearFilters={onClearFilters}
        />
      ) : (
        <>
          {careRows.length > 0 && (
            <ReportsChartGrid>
              <ReportsChartCol lg={12}>
                <ReportChartCard title={pieTitle} subtitle={pieDesc} height={320}>
                  <ClinicalDoughnutChart data={carePie} />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          )}

          {careRows.length > 0 && (
            <>
              <h3 className="hd-reports-table-title">{tableTitle}</h3>
              <StatisticalDataTable
                columns={careTableCols}
                rows={careRows}
                rowKey="catalog_id"
                maxValueKey="action_count"
                loading={loading}
                emptyLabel={emptyMessage}
                loadingLabel={copy.LOADING}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

export function NursingTestReportSection({
  data,
  summary,
  loading,
  copy,
  showKpis = true,
  emptyHint = '',
  clearFiltersLabel = '',
  onClearFilters = null
}) {
  const testRows = useMemo(
    () => computeShares(data?.nursing?.test_ranking || [], 'result_count'),
    [data]
  );

  const testTableCols = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    {
      key: 'test_label',
      label: copy.COL_NURSING_TEST,
      className: 'fw-semibold',
      render: (r) => r.test_label
    },
    statRankColumn(copy.COL_RESULTS, 'result_count'),
    statPercentColumn(copy.COL_SHARE),
    statNumberColumn(copy.COL_CONSULTATIONS, 'consultation_count')
  ];

  const empty = summary.nursing_test_results === 0;

  return (
    <>
      {showKpis ? (
        <Row className="g-3 mb-4">
          <Col sm={4}>
            <HeadDoctorStatCard
              title={copy.KPI_NURSING_TESTS}
              value={summary.nursing_test_results}
              icon="check"
              tone="amber"
              loading={loading}
            />
          </Col>
        </Row>
      ) : null}

      {loading ? (
        <ReportLoadingPanel copy={copy} />
      ) : empty ? (
        <ReportEmptyPanel
          message={copy.EMPTY_NURSING_TESTS}
          hint={emptyHint}
          clearFiltersLabel={clearFiltersLabel}
          onClearFilters={onClearFilters}
        />
      ) : (
        <>
          {testRows.length > 0 && (
            <>
              <h3 className="hd-reports-table-title">{copy.TABLE_NURSING_TEST_TITLE}</h3>
              <StatisticalDataTable
                columns={testTableCols}
                rows={testRows}
                rowKey="catalog_id"
                maxValueKey="result_count"
                loading={loading}
                emptyLabel={copy.EMPTY_NURSING_TESTS}
                loadingLabel={copy.LOADING}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

export function NursingVaccineReportSection({
  data,
  summary,
  loading,
  copy,
  showKpis = true,
  emptyHint = '',
  clearFiltersLabel = '',
  onClearFilters = null
}) {
  const vaccineRows = useMemo(
    () => computeShares(data?.nursing?.vaccine_ranking || [], 'administration_count'),
    [data]
  );

  const vaccinePie = useMemo(() => {
    const pie = topNWithOthers(
      vaccineRows,
      'administration_count',
      'vaccine_label',
      5,
      copy.CHART_OTHERS
    );
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [vaccineRows, copy.CHART_OTHERS]);

  const vaccineTableCols = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    {
      key: 'vaccine_label',
      label: copy.COL_NURSING_VACCINE,
      className: 'fw-semibold',
      render: (r) => r.vaccine_label
    },
    statRankColumn(copy.COL_ADMINISTRATIONS, 'administration_count'),
    statPercentColumn(copy.COL_SHARE),
    statNumberColumn(copy.COL_CONSULTATIONS, 'consultation_count')
  ];

  const empty = summary.nursing_vaccinations === 0;

  return (
    <>
      {showKpis ? (
        <Row className="g-3 mb-4">
          <Col sm={4}>
            <HeadDoctorStatCard
              title={copy.KPI_NURSING_VACCINES}
              value={summary.nursing_vaccinations}
              icon="shield"
              tone="teal"
              loading={loading}
            />
          </Col>
        </Row>
      ) : null}

      {loading ? (
        <ReportLoadingPanel copy={copy} />
      ) : empty ? (
        <ReportEmptyPanel
          message={copy.EMPTY_NURSING_VACCINES}
          hint={emptyHint}
          clearFiltersLabel={clearFiltersLabel}
          onClearFilters={onClearFilters}
        />
      ) : (
        <>
          {vaccineRows.length > 0 && (
            <ReportsChartGrid>
              <ReportsChartCol lg={12}>
                <ReportChartCard
                  title={copy.CHART_NURSING_VACCINE_PIE_TITLE}
                  subtitle={copy.CHART_NURSING_VACCINE_PIE_DESC}
                  height={320}
                >
                  <ClinicalDoughnutChart data={vaccinePie} />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          )}

          {vaccineRows.length > 0 && (
            <>
              <h3 className="hd-reports-table-title">{copy.TABLE_NURSING_VACCINE_TITLE}</h3>
              <StatisticalDataTable
                columns={vaccineTableCols}
                rows={vaccineRows}
                rowKey="catalog_id"
                maxValueKey="administration_count"
                loading={loading}
                emptyLabel={copy.EMPTY_NURSING_VACCINES}
                loadingLabel={copy.LOADING}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

export function NursingFamilyPlanningReportSection({
  data,
  summary,
  loading,
  copy,
  showKpis = true,
  emptyHint = '',
  clearFiltersLabel = '',
  onClearFilters = null
}) {
  const familyPlanningRows = useMemo(
    () => computeShares(data?.nursing?.family_planning_ranking || [], 'action_count'),
    [data]
  );

  const familyPlanningPie = useMemo(() => {
    const pie = topNWithOthers(
      familyPlanningRows,
      'action_count',
      'family_planning_label',
      5,
      copy.CHART_OTHERS
    );
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [familyPlanningRows, copy.CHART_OTHERS]);

  const familyPlanningTableCols = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    {
      key: 'family_planning_label',
      label: copy.COL_NURSING_FAMILY_PLANNING,
      className: 'fw-semibold',
      render: (r) => r.family_planning_label
    },
    statRankColumn(copy.COL_ACTIONS, 'action_count'),
    statPercentColumn(copy.COL_SHARE),
    statNumberColumn(copy.COL_CONSULTATIONS, 'consultation_count')
  ];

  const empty = summary.nursing_family_planning_actions === 0;

  return (
    <>
      {showKpis ? (
        <Row className="g-3 mb-4">
          <Col sm={4}>
            <HeadDoctorStatCard
              title={copy.KPI_NURSING_FAMILY_PLANNING}
              value={summary.nursing_family_planning_actions}
              icon="heart"
              tone="teal"
              loading={loading}
            />
          </Col>
        </Row>
      ) : null}

      {loading ? (
        <ReportLoadingPanel copy={copy} />
      ) : empty ? (
        <ReportEmptyPanel
          message={copy.EMPTY_NURSING_FAMILY_PLANNING}
          hint={emptyHint}
          clearFiltersLabel={clearFiltersLabel}
          onClearFilters={onClearFilters}
        />
      ) : (
        <>
          {familyPlanningRows.length > 0 && (
            <ReportsChartGrid>
              <ReportsChartCol lg={12}>
                <ReportChartCard
                  title={copy.CHART_NURSING_FAMILY_PLANNING_PIE_TITLE}
                  subtitle={copy.CHART_NURSING_FAMILY_PLANNING_PIE_DESC}
                  height={320}
                >
                  <ClinicalDoughnutChart data={familyPlanningPie} />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          )}

          {familyPlanningRows.length > 0 && (
            <>
              <h3 className="hd-reports-table-title">{copy.TABLE_NURSING_FAMILY_PLANNING_TITLE}</h3>
              <StatisticalDataTable
                columns={familyPlanningTableCols}
                rows={familyPlanningRows}
                rowKey="family_planning_label"
                maxValueKey="action_count"
                loading={loading}
                emptyLabel={copy.EMPTY_NURSING_FAMILY_PLANNING}
                loadingLabel={copy.LOADING}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
